import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { t } from 'i18next';
import { logger } from '@sentry/react';
import { useUpdateNodeInternals } from 'reactflow';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Flex, FlexCenHorVer } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { I18N_KEYS } from '@/language/keys';
import { LinkIcon } from '@/UI/Icons';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { LoadingCircle } from '@/UI/Animations/LoadingCircle';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { ImageFileViewer, NodeViewer } from '../Shared/FileViewer';
import { useFigmaCompositorView, useOnInputsChange } from '../../Recipe/FlowContext';
import { getOrCreateFigmaApiClient } from '../../Recipe/FlowComponents/Editor/FigmaCompositor/figmaApiClient';
import { getFigmaFileUrl, getFormattedNodeId, useSyncToFigma } from './utils';
import type { FigmaCompositorData } from '@/types/node';

function LoadingState({ isOverlay = false }: { isOverlay?: boolean } = {}) {
  return (
    <FlexCenHorVer
      className={isOverlay ? '' : 'media-container-dark'}
      sx={
        isOverlay
          ? { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }
          : { width: '100%', height: '200px' }
      }
    >
      <LoadingCircle size={32} color="white" />
    </FlexCenHorVer>
  );
}

function Footer({
  id,
  handleOpenEditWindow,
  handleRefresh,
  handleAddInputHandle,
}: {
  id: string;
  handleOpenEditWindow: () => void;
  handleRefresh: () => void;
  handleAddInputHandle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid={`open-compositor-edit-button-container-${id}`}
      sx={{
        opacity: 1,
        pointerEvents: 'auto',
        cursor: 'auto',
      }}
    >
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonContained mode="text" size="small" onClick={handleAddInputHandle}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.COMPOSITOR.ADD_ANOTHER_LAYER_CTA)}
        </ButtonContained>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', marginLeft: 1 }}>
          <ButtonContained
            mode="outlined"
            onClick={handleRefresh}
            sx={{ height: '36px', borderRadius: 2, px: 1.5, py: 1, marginRight: 1 }}
          >
            {t(I18N_KEYS.FIGMA_COMPOSITOR.REFRESH)}
          </ButtonContained>
          <ButtonContained
            mode="outlined"
            onClick={handleOpenEditWindow}
            sx={{ height: '36px', borderRadius: 2, px: 1.5, py: 1 }}
            startIcon={<LinkIcon height="16px" width="16px" />}
          >
            {t(I18N_KEYS.FIGMA_COMPOSITOR.OPEN_IN_FIGMA)}
          </ButtonContained>
        </Flex>
      </Flex>
    </Box>
  );
}

function FigmaCompositorContent({
  id,
  data,
  handleAddInputHandle,
  handleOpenEditWindow,
  handleRefresh,
  isInitialImagePreviewLoading,
  isImagePreviewUpdating,
}: {
  id: string;
  data: FigmaCompositorData;
  handleOpenEditWindow: () => void;
  handleRefresh: () => void;
  handleAddInputHandle: () => void;
  isInitialImagePreviewLoading: boolean;
  isImagePreviewUpdating: boolean;
}) {
  const outputImage = data.outputImage;

  let nodeViewer: React.ReactNode;
  if (isInitialImagePreviewLoading) {
    nodeViewer = <LoadingState />;
  } else if (outputImage) {
    nodeViewer = (
      <ImageFileViewer
        url={outputImage.url}
        thumbnailUrl={outputImage.thumbnailUrl}
        width={outputImage.width}
        height={outputImage.height}
      />
    );
  } else {
    nodeViewer = <NodeViewer id={id} />;
  }

  return (
    <>
      <Flex sx={{ cursor: 'default', position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        {nodeViewer}
        {isImagePreviewUpdating && <LoadingState isOverlay />}
      </Flex>
      <Footer
        id={id}
        handleOpenEditWindow={handleOpenEditWindow}
        handleRefresh={handleRefresh}
        handleAddInputHandle={handleAddInputHandle}
      />
    </>
  );
}

export function FigmaCompositorNode({ id, data }: { id: string; data: FigmaCompositorData }) {
  const view = useFigmaCompositorView(id);
  const { updateSnackbarData } = useGlobalStore();
  const recipeId = useWorkflowStore((state) => state.recipe.id);
  const updateNodeInternals = useUpdateNodeInternals();

  const { fileKey, nodeId: figmaNodeId } = data.options;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { mutate: connectNode, isPending: isConnecting } = useMutation({
    mutationFn: async () => {
      const response = await getOrCreateFigmaApiClient().connectNode(fileKey!, figmaNodeId!, recipeId, id);
      return response.thumbnail;
    },
    onSuccess: (thumbnail) => {
      view.setOutputImage({
        url: thumbnail.url,
        width: thumbnail.width,
        height: thumbnail.height,
        thumbnailUrl: thumbnail.url,
        publicId: undefined,
        id: undefined,
      });
    },
    onError: (err) => {
      // TODO: properly handle errors
      logger.error('Failed to connect Figma node', { error: String(err) });
      const isAlreadyConnected = isAxiosError(err) && err.response?.status === 409;
      updateSnackbarData({
        text: t(
          isAlreadyConnected
            ? I18N_KEYS.FIGMA_COMPOSITOR.ALREADY_CONNECTED_ERROR
            : I18N_KEYS.FIGMA_COMPOSITOR.LOAD_IMAGE_ERROR,
        ),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });

  // Ideally we make the connection request in the paste handler (see tryPasteFigmaCompositorNodes),
  // but we don't have a great way to do mutations that affect views there
  useEffect(() => {
    if (fileKey && figmaNodeId && !data.outputImage) {
      connectNode();
    }
  }, [fileKey, figmaNodeId, data.outputImage, connectNode]);

  const handleRefresh = () => {
    setIsRefreshing(true);

    // TODO: switch to the GET API call
    connectNode(undefined, {
      onSettled: () => setIsRefreshing(false),
    });
  };

  const handleOpenEditWindow = () => {
    const formattedNodeId = getFormattedNodeId(data.options.nodeId || '');
    window.open(getFigmaFileUrl(data?.options?.fileKey || '', formattedNodeId), '_blank');
  };

  const handleAddInputHandle = () => {
    view.addNodeInputHandle();
    updateNodeInternals(id);
  };

  const triggerSyncToFigma = useSyncToFigma(view, data.options, setIsRefreshing);
  useOnInputsChange(id, triggerSyncToFigma);

  return (
    <DynamicNode2 id={id} data={data} icon={<FigmaLogo />}>
      <FigmaCompositorContent
        id={id}
        data={data}
        handleRefresh={() => void handleRefresh()}
        isInitialImagePreviewLoading={isConnecting && !data.outputImage}
        isImagePreviewUpdating={(isConnecting && !!data.outputImage) || isRefreshing}
        handleOpenEditWindow={handleOpenEditWindow}
        handleAddInputHandle={handleAddInputHandle}
      />
    </DynamicNode2>
  );
}
